import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useToast } from '@kinesis/bungle';
import { Center } from '@/components/center';
import { Loading } from '@/components/loading';
import { useSessionQuery } from '@/api/login';
import {
  ShareAccepted,
  useAcceptShareMutation,
  useDeclineShareMutation,
  useShareAcceptDetailsQuery,
} from '@/api/share';
import { sharedPathnameFor } from '@/services/launch';
import { ShareMessage } from './share-message';

// A claim lands on the thing that was shared. Diplomat names it by
// namespace and key; zoo maps that to a path. A namespace zoo has no path
// for is not guessed at — the claim still succeeded, so the confirmation
// stands in for the destination.
const openShared = (accepted: ShareAccepted) => {
  const pathname = sharedPathnameFor(accepted?.object);
  if (!pathname) {
    return false;
  }
  window.location.assign(pathname);
  return true;
};

// The emailed share link lands here. There is no public details view: a
// share is redeemed as an identity, so a visitor without a session is sent
// to log in or create an account, the link parked to come back to. The
// share's state at redeem time is authoritative — the emailed copy was
// written when the share was issued and may have assumed otherwise.
const ShareAccept = () => {
  const { token = '' } = useParams<{ token: string }>();
  const { data: session, isLoading: isSessionLoading } = useSessionQuery();
  const loggedIn = !!session?.accessToken;
  const {
    data: details,
    error: detailsError,
    isLoading: isDetailsLoading,
  } = useShareAcceptDetailsQuery(token, { skip: !loggedIn });
  const [accept, { isLoading: isAccepting }] = useAcceptShareMutation();
  const [decline, { isLoading: isDeclining }] = useDeclineShareMutation();
  const [claimed, setClaimed] = useState(false);
  const [spentByAnother, setSpentByAnother] = useState(false);
  const toast = useToast('globalTop');

  const onAccept = useCallback(async () => {
    try {
      const accepted = await accept(token).unwrap();
      setClaimed(true);
      if (!openShared(accepted)) {
        setClaimed(false);
        toast('We couldn’t open this share. Please try again.', {
          variant: 'error',
          duration: 6000,
        });
      }
    } catch (e) {
      toast('We couldn’t open this share. Please try again.', {
        variant: 'error',
        duration: 6000,
      });
    }
  }, [accept, toast, token]);

  const onDecline = useCallback(async () => {
    try {
      await decline(token).unwrap();
      window.location.assign('/');
    } catch (e) {
      toast('Error declining share.', {
        variant: 'error',
        duration: 6000,
      });
    }
  }, [decline, toast, token]);

  const onLogin = useCallback(() => {
    localStorage.setItem('kinesis:redirect', window.location.pathname);
    window.location.assign('/');
  }, []);

  const onCreateAccount = useCallback(() => {
    localStorage.setItem('kinesis:redirect', window.location.pathname);
    window.location.assign('/registration');
  }, []);

  // A spent token accepts idempotently for whoever spent it: diplomat
  // answers success for the original acceptor (and the claim lands on the
  // shared thing as usual) and 404 for anyone else.
  useEffect(() => {
    if (details?.state !== 'spent' || claimed || spentByAnother) {
      return;
    }
    setClaimed(true);
    accept(token)
      .unwrap()
      .then((accepted) => {
        if (!openShared(accepted)) {
          setClaimed(false);
          setSpentByAnother(true);
        }
      })
      .catch(() => {
        setClaimed(false);
        setSpentByAnother(true);
      });
  }, [accept, claimed, details, spentByAnother, token]);

  if (isSessionLoading || (loggedIn && isDetailsLoading)) {
    return <Loading />;
  }

  if (!loggedIn) {
    return (
      <Center>
        <ShareMessage
          title='Shared with you'
          actions={[
            { label: 'Log in', onClick: onLogin },
            {
              label: 'Create an account',
              onClick: onCreateAccount,
              variant: 'secondary',
            },
          ]}
        >
          You’ve been shared a disclosure on Kinesis. Log in, or create an
          account, to see and accept it.
        </ShareMessage>
      </Center>
    );
  }

  const notFound = (detailsError as any)?.status === 404;
  const label = details?.label ?? 'this';
  const sharer = details?.sharerName;

  return (
    <Center>
      {details?.state === 'pending' && !claimed && (
        <ShareMessage
          title='Shared with you'
          organisation={{
            name: details.ownerName ?? undefined,
            avatar: details.ownerAvatarUrl ?? undefined,
          }}
          actions={[
            {
              label: 'Accept',
              onClick: onAccept,
              loading: isAccepting,
            },
            {
              label: 'Decline',
              onClick: onDecline,
              variant: 'secondary',
              loading: isDeclining,
            },
          ]}
        >
          {sharer ? `${sharer} shared ` : 'Someone shared '}
          <strong>{label}</strong> with you.
        </ShareMessage>
      )}
      {claimed && <Loading />}
      {details?.state === 'spent' && spentByAnother && (
        <ShareMessage title='Already claimed'>
          This share has already been claimed.
        </ShareMessage>
      )}
      {details?.state === 'expired' && (
        <ShareMessage title='Share expired'>
          This share has expired. Ask whoever shared it with you to share it
          again.
        </ShareMessage>
      )}
      {notFound && (
        <ShareMessage title='Share not found'>
          This share isn’t available. Ask whoever shared it with you to share it
          again.
        </ShareMessage>
      )}
      {!details && detailsError && !notFound && (
        <ShareMessage title='Something went wrong'>
          We couldn’t open this share. Please try again.
        </ShareMessage>
      )}
    </Center>
  );
};

export { ShareAccept };
